/**
 * Subscription Plans Page - Admin Portal
 * Manage subscription plans for the SaaS platform
 *
 * Architecture (PRODUCT_CATALOG_CLEANUP_PROPOSAL.md, Phase 9):
 * - Plans select from the 9 sellable products (PRODUCT_CODES) only.
 * - Capabilities bundled with CRM (BUNDLED_WITH_CRM) are shown
 *   informationally and are never independently selected here -- they
 *   follow automatically whenever CRM is included.
 * - Field Service (LEGACY_CODES) is not offered in this picker for new/
 *   edited plans; a plan that already has it keeps it untouched.
 * - Licenses are separate (seat-based access control).
 */
import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { PermissionGate } from '../components';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import {
  CreditCard,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Loader2,
  CheckCircle,
  Users,
  Database,
  Zap,
  Building2,
  DollarSign,
  ChevronDown,
  ChevronRight,
  Package,
  Layers,
  TrendingUp,
  Star,
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import StatGrid from '../../../components/admin/StatGrid';
import MetricCard from '../../../components/admin/MetricCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin`;

// Product classification comes from the backend (`product_category`,
// sourced from PRODUCT_CODES / BUNDLED_WITH_CRM / LEGACY_CODES) -- this
// file no longer maintains its own category grouping. "crm" is a legacy
// alias of "sales_console" (flagged `duplicate_of` by the backend) and is
// filtered out wherever products are listed, so "CRM" appears once.
const PRODUCT_ICONS = {
  sales_console: Building2, task_manager: Package, form_builder: Layers,
  flow_builder: Zap, survey_builder: Users, booking: CreditCard,
  chatbot_manager: Zap, docflow: Database, reporting: Package,
};
const getModuleIcon = (apiName) => PRODUCT_ICONS[apiName] || Package;

const getSellableProducts = (modules) =>
  modules
    .filter((m) => m.product_category === 'sellable' && !m.duplicate_of)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

const getBundledCapabilities = (modules) =>
  modules
    .filter((m) => m.product_category === 'bundled')
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

const planHasCrm = (enabledModules) =>
  (enabledModules || []).some((m) => m === 'sales_console' || m === 'crm');

const SubscriptionsPage = () => {
  const { getAdminToken } = useAdminAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableModules, setAvailableModules] = useState([]);
  const [standardLimits, setStandardLimits] = useState([]);
  const [expandedPlan, setExpandedPlan] = useState(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedPlan, setSelectedPlan] = useState(null);
  // Phase 10 (LICENSE_AND_PLAN_FORM_FIELD_AUDIT.md / FINAL_VERIFICATION_AUDIT.md):
  // `api_limit_daily` is intentionally absent from this state -- confirmed
  // structurally disconnected (create_tenant() never copies it onto a
  // tenant document, so it can never reach the place that would enforce
  // it). Omitting it from create/update payloads leaves the backend
  // model's own default in place for new plans, and leaves an existing
  // plan's stored value untouched on edit.
  const [formData, setFormData] = useState({
    name: '',
    api_name: '',
    description: '',
    price_monthly: 0,
    price_yearly: 0,
    seat_limit: 10,
    storage_limit_mb: 512,
    enabled_modules: [],
    default_limits: {},
    is_active: true,
    is_public: true,
    sort_order: 0
  });
  // Seat Limit / Storage each have exactly one primary field; Maximum Users
  // / Maximum Storage (GB) are auto-populated from them at save time
  // (see handleSave) unless the admin explicitly opts into an independent
  // override here. Detected as "already overridden" on edit whenever the
  // plan's default_limits already has an explicit value for that key.
  const [overrideMaxUsers, setOverrideMaxUsers] = useState(false);
  const [overrideMaxStorage, setOverrideMaxStorage] = useState(false);
  const [showAdvancedLimits, setShowAdvancedLimits] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${ADMIN_API}/plans?include_inactive=true`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` }
      });
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${ADMIN_API}/modules/available`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` }
      });
      setAvailableModules(response.data.modules || []);
    } catch (error) {
      console.error('Failed to fetch modules:', error);
    }
  };

  // Drives the Default Limits section below — the same catalog the Limits &
  // Quotas tab in Tenant Detail reads, so a new quota key added there shows
  // up here automatically with no frontend code change.
  const fetchStandardLimits = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/control-plane/limits/standard`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` }
      });
      setStandardLimits(response.data.limits || []);
    } catch (error) {
      console.error('Failed to fetch standard limits:', error);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchModules();
    fetchStandardLimits();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only
  }, []);

  const handleCreate = () => {
    setDialogMode('create');
    setSelectedPlan(null);
    setFormData({
      name: '',
      api_name: '',
      description: '',
      price_monthly: 0,
      price_yearly: 0,
      seat_limit: 10,
      storage_limit_mb: 512,
      enabled_modules: [],
      default_limits: {},
      is_active: true,
      is_public: true,
      sort_order: plans.length
    });
    setOverrideMaxUsers(false);
    setOverrideMaxStorage(false);
    setShowAdvancedLimits(false);
    setDialogOpen(true);
  };

  const handleEdit = (plan) => {
    setDialogMode('edit');
    setSelectedPlan(plan);
    const existingLimits = plan.default_limits || {};
    setFormData({
      name: plan.name,
      api_name: plan.api_name,
      description: plan.description || '',
      price_monthly: plan.price_monthly || 0,
      price_yearly: plan.price_yearly || 0,
      seat_limit: plan.seat_limit || 5,
      storage_limit_mb: plan.storage_limit_mb || 512,
      enabled_modules: plan.enabled_modules || [],
      default_limits: existingLimits,
      is_active: plan.is_active !== false,
      is_public: plan.is_public !== false,
      sort_order: plan.sort_order || 0
    });
    // An existing plan that already has an explicit MAX_USERS/MAX_STORAGE_GB
    // value is treated as already-overridden -- surface it rather than
    // silently discarding a deliberately-set number.
    setOverrideMaxUsers(existingLimits.MAX_USERS !== undefined);
    setOverrideMaxStorage(existingLimits.MAX_STORAGE_GB !== undefined);
    setShowAdvancedLimits(false);
    setDialogOpen(true);
  };

  const setLimitDefault = (limitKey, rawValue) => {
    setFormData(prev => {
      const next = { ...prev.default_limits };
      if (rawValue === '' || rawValue === null || rawValue === undefined) {
        delete next[limitKey];
      } else {
        const num = parseInt(rawValue, 10);
        if (!Number.isNaN(num)) next[limitKey] = num;
      }
      return { ...prev, default_limits: next };
    });
  };

  // Builds the actual submitted payload: Maximum Users / Maximum Storage
  // (GB) are auto-populated from Seat Limit / Storage the moment they're
  // not explicitly overridden, so the two concepts never silently drift
  // apart for a plan an admin only ever sees the primary field for.
  const buildSavePayload = () => {
    const payload = { ...formData, default_limits: { ...formData.default_limits } };
    payload.default_limits.MAX_USERS = overrideMaxUsers
      ? formData.default_limits.MAX_USERS
      : formData.seat_limit;
    payload.default_limits.MAX_STORAGE_GB = overrideMaxStorage
      ? formData.default_limits.MAX_STORAGE_GB
      : Math.round((formData.storage_limit_mb / 1024) * 100) / 100;
    // An override toggle left on with no value typed yet shouldn't submit
    // `undefined` -- fall back to the auto-derived number rather than send
    // a broken payload.
    if (payload.default_limits.MAX_USERS === undefined) payload.default_limits.MAX_USERS = formData.seat_limit;
    if (payload.default_limits.MAX_STORAGE_GB === undefined) {
      payload.default_limits.MAX_STORAGE_GB = Math.round((formData.storage_limit_mb / 1024) * 100) / 100;
    }
    return payload;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = buildSavePayload();
      if (dialogMode === 'create') {
        await axios.post(`${ADMIN_API}/plans`, payload, {
          headers: { Authorization: `Bearer ${getAdminToken()}` }
        });
        toast.success(`Plan "${formData.name}" created`);
      } else {
        await axios.patch(`${ADMIN_API}/plans/${selectedPlan.id}`, payload, {
          headers: { Authorization: `Bearer ${getAdminToken()}` }
        });
        toast.success(`Plan "${formData.name}" updated`);
      }
      setDialogOpen(false);
      fetchPlans();
    } catch (error) {
      console.error('Failed to save plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${ADMIN_API}/plans/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` }
      });
      toast.success(`Plan "${deleteTarget.name}" deactivated`);
      setDeleteTarget(null);
      fetchPlans();
    } catch (error) {
      console.error('Failed to delete plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to deactivate plan');
    } finally {
      setDeleting(false);
    }
  };

  // handleSeedPlans removed. It POSTed to `/plans/seed`, a route that no
  // longer exists: seeding was deleted deliberately when the commercial model
  // was settled — "the system should give us the tools to construct commercial
  // offerings, not decide what those offerings are". The buttons survived the
  // route and 404'd on click, offering an operator a shortcut the product does
  // not have.

  const toggleModule = (moduleApiName) => {
    setFormData(prev => {
      const modules = new Set(prev.enabled_modules);
      if (modules.has(moduleApiName)) {
        modules.delete(moduleApiName);
      } else {
        modules.add(moduleApiName);
      }
      return { ...prev, enabled_modules: Array.from(modules) };
    });
  };

  // Get module details by api_name
  const getModuleInfo = (apiName) => {
    return availableModules.find(m => m.api_name === apiName);
  };

  // Render a plan's included products as badges (read-only summary on the
  // plan list cards). Bundled capabilities are never listed individually
  // here -- only summarized once as "+ CRM bundle" when CRM is included,
  // matching the informational (never separately toggled) treatment they
  // get in the editor below.
  const renderPlanModules = (enabledModules) => {
    const moduleDetails = (enabledModules || [])
      .map(apiName => getModuleInfo(apiName))
      .filter(Boolean);
    const products = moduleDetails.filter(m => m.product_category === 'sellable' && !m.duplicate_of);
    // Named, not assumed: Field Service stopped being the only legacy code
    // when Form Builder and Survey Builder were folded into Engage.
    const legacy = moduleDetails.filter(m => m.product_category === 'legacy');
    const hasLegacy = legacy.length > 0;

    return (
      <div className="space-y-2 py-2">
        <div className="flex flex-wrap gap-1.5">
          {products.map(module => (
            <Badge
              key={module.api_name}
              variant="outline"
              className="text-xs bg-indigo-50 border-indigo-200 text-indigo-700"
            >
              {module.name}
              {module.is_premium && <span className="ml-1 text-amber-500">★</span>}
            </Badge>
          ))}
          {products.length === 0 && !planHasCrm(enabledModules) && (
            <p className="text-xs text-slate-400 italic">No products assigned</p>
          )}
        </div>
        {planHasCrm(enabledModules) && (
          <p className="text-xs text-slate-400">+ CRM bundle (Schema Builder, App Manager, and 7 more)</p>
        )}
        {hasLegacy && (
          <Badge variant="outline" className="text-xs bg-amber-50 border-amber-200 text-amber-700">
            Legacy: {legacy.map(m => m.name).join(', ')}
          </Badge>
        )}
      </div>
    );
  };

  // Render the product picker for the create/edit dialog. Only sellable
  // products (Category A) are independently toggleable. Capabilities
  // bundled with CRM are shown informationally the moment CRM is selected,
  // never as their own toggle. Field Service (legacy) is excluded from
  // this picker entirely -- not offered for new/edited plans -- but is
  // never stripped from formData.enabled_modules if a plan already has it,
  // since toggleModule() only ever mutates the array via an explicit click
  // on a rendered card.
  const renderModuleSelector = () => {
    const products = getSellableProducts(availableModules);
    const bundled = getBundledCapabilities(availableModules);
    const selectedCount = products.filter(m => formData.enabled_modules.includes(m.api_name)).length;
    const hasCrmSelected = planHasCrm(formData.enabled_modules);

    return (
      <div className="space-y-4">
        <div className="border rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-indigo-50 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Products</span>
              <Badge variant="secondary" className="text-xs">
                {selectedCount}/{products.length}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-6"
              onClick={() => {
                const allSelected = products.every(m => formData.enabled_modules.includes(m.api_name));
                setFormData(prev => ({
                  ...prev,
                  enabled_modules: allSelected
                    ? prev.enabled_modules.filter(m => !products.find(p => p.api_name === m))
                    : [...new Set([...prev.enabled_modules, ...products.map(m => m.api_name)])]
                }));
              }}
            >
              {products.every(m => formData.enabled_modules.includes(m.api_name)) ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
          <div className="p-2 grid grid-cols-2 gap-1.5">
            {products.map((module) => {
              const Icon = getModuleIcon(module.api_name);
              return (
                <div
                  key={module.api_name}
                  className={`p-2 border rounded cursor-pointer transition-colors text-sm ${
                    formData.enabled_modules.includes(module.api_name)
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => toggleModule(module.api_name)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Icon className="h-3.5 w-3.5 text-slate-500" />
                      <span className="font-medium">{module.name}</span>
                      {module.is_premium && (
                        <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1">Premium</Badge>
                      )}
                    </div>
                    {formData.enabled_modules.includes(module.api_name) && (
                      <CheckCircle className="h-3.5 w-3.5 text-indigo-600" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {hasCrmSelected && (
          <div className="border rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b">
              <span className="text-sm font-medium">Included with CRM</span>
              <p className="text-xs text-slate-500 mt-0.5">
                Automatically available — not sold or selected separately.
              </p>
            </div>
            <div className="p-2 grid grid-cols-2 gap-1.5">
              {bundled.map((module) => (
                <div
                  key={module.api_name}
                  className="flex items-center gap-1.5 p-2 border border-slate-200 rounded bg-slate-50 text-slate-500 text-sm"
                >
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                  {module.name}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Analytics: MRR/ARR are derived from this page's own plans list
  // (Σ price_monthly × tenant_count, and that × 12 for ARR) -- the same
  // fields already rendered in the plan cards below. Revenue Trend and
  // Conversion aren't shown: no time-series or signup-funnel endpoint
  // exists to back them honestly.
  const activePlansList = plans.filter((p) => p.is_active !== false);
  const mrr = activePlansList.reduce((sum, p) => sum + (p.price_monthly || 0) * (p.tenant_count || 0), 0);
  const arr = mrr * 12;
  const mostPopularPlan = activePlansList.reduce(
    (best, p) => ((p.tenant_count || 0) > (best?.tenant_count || 0) ? p : best),
    null
  );
  const totalTenantsOnPlans = activePlansList.reduce((sum, p) => sum + (p.tenant_count || 0), 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 animate-fade-in-up">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Subscription Plans</h1>
          <p className="text-muted-foreground mt-1">
            Define plans and the products they include
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PermissionGate permission="plan:write">
            <Button
              className="bg-primary hover:bg-primary/90 gap-2"
              onClick={handleCreate}
              data-testid="create-plan-btn"
            >
              <Plus className="h-4 w-4" />
              Create Plan
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Analytics */}
      {loading && plans.length === 0 ? (
        <StatGrid cols={4}>
          {Array.from({ length: 4 }).map((_, i) => <MetricCard key={i} loading />)}
        </StatGrid>
      ) : (
        <StatGrid cols={4}>
          <MetricCard
            label="MRR"
            value={mrr}
            format={(v) => `$${Math.round(v).toLocaleString()}`}
            icon={DollarSign}
            accent="success"
            hint="Σ (plan monthly price × tenants on that plan) across active plans. List-price estimate, not confirmed Stripe revenue."
          />
          <MetricCard
            label="ARR"
            value={arr}
            format={(v) => `$${Math.round(v).toLocaleString()}`}
            icon={TrendingUp}
            accent="violet"
            hint="MRR × 12."
          />
          <MetricCard
            label="Most Popular Plan"
            value={mostPopularPlan?.tenant_count || 0}
            format={(v) => `${mostPopularPlan?.name || '—'}`}
            icon={Star}
            accent="warning"
            hint={mostPopularPlan ? `${mostPopularPlan.tenant_count || 0} tenants` : 'No tenants on any active plan yet'}
          />
          <MetricCard label="Tenants on Active Plans" value={totalTenantsOnPlans} icon={Building2} accent="sky" />
        </StatGrid>
      )}

      {/* Info Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Layers className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium text-foreground">Product-Based Plans</p>
              <p className="text-sm text-muted-foreground mt-1">
                Plans reference sellable products. Capabilities bundled with CRM (Schema Builder,
                App Manager, and others) are included automatically and aren't selected individually.
                Licenses (seats) control user access separately.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plans List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">
            All Plans ({plans.length})
          </CardTitle>
          <CardDescription>
            Click on a plan row to see all included products
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CreditCard className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No plans configured</p>
              <p className="text-sm mt-1">
                Plans are defined here deliberately — nothing is seeded for you.
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {plans.map((plan) => (
                <Collapsible
                  key={plan.id}
                  open={expandedPlan === plan.id}
                  onOpenChange={(open) => setExpandedPlan(open ? plan.id : null)}
                >
                  <div 
                    className="flex items-center justify-between p-4 hover:bg-slate-50 cursor-pointer"
                    data-testid={`plan-row-${plan.id}`}
                  >
                    <CollapsibleTrigger className="flex items-center gap-4 flex-1">
                      <div className="flex items-center gap-2">
                        {expandedPlan === plan.id ? (
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        )}
                        <div>
                          <p className="font-medium text-slate-900">{plan.name}</p>
                          <p className="text-xs text-slate-500">{plan.api_name}</p>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    
                    <div className="flex items-center gap-6">
                      {/* Pricing */}
                      <div className="text-right">
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-3.5 w-3.5 text-slate-400" />
                          <span className="font-semibold">{plan.price_monthly}</span>
                          <span className="text-slate-400 text-sm">/mo</span>
                        </div>
                      </div>
                      
                      {/* Limits */}
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          <span>{plan.seat_limit} seats</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Database className="h-3 w-3" />
                          <span>{Math.round((plan.storage_limit_mb / 1024) * 100) / 100} GB</span>
                        </div>
                      </div>
                      
                      {/* Product Count */}
                      <Badge variant="outline" className="text-xs">
                        <Package className="h-3 w-3 mr-1" />
                        {(plan.enabled_modules || []).filter(m => {
                          const info = getModuleInfo(m);
                          return info && info.product_category === 'sellable' && !info.duplicate_of;
                        }).length} products
                      </Badge>
                      
                      {/* Status */}
                      {plan.is_active !== false ? (
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-700">Inactive</Badge>
                      )}
                      
                      {/* Tenant Count */}
                      <div className="flex items-center gap-1 text-sm text-slate-500 min-w-[60px]">
                        <Building2 className="h-3.5 w-3.5" />
                        <span>{plan.tenant_count || 0}</span>
                      </div>
                      
                      {/* Actions */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(plan)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDeleteTarget(plan)} className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Deactivate
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  
                  <CollapsibleContent>
                    <div className="px-4 pb-4 pt-0 ml-10 border-l-2 border-indigo-100">
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3">
                          Included Products
                        </p>
                        {renderPlanModules(plan.enabled_modules || [])}
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Plan Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialogMode === 'create' ? 'Create Plan' : 'Edit Plan'}</DialogTitle>
            <DialogDescription>
              {dialogMode === 'create'
                ? 'Create a new subscription plan and select the products it includes'
                : 'Edit plan details and product selection'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Plan Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Professional"
                  data-testid="plan-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="api_name">API Name *</Label>
                <Input
                  id="api_name"
                  value={formData.api_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, api_name: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                  placeholder="e.g., professional"
                  disabled={dialogMode === 'edit'}
                  data-testid="plan-api-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the plan"
                rows={2}
              />
            </div>

            {/* Pricing */}
            <div>
              <h4 className="text-sm font-medium mb-3">Pricing</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price_monthly">Monthly Price ($)</Label>
                  <Input
                    id="price_monthly"
                    type="number"
                    min="0"
                    value={formData.price_monthly}
                    onChange={(e) => setFormData(prev => ({ ...prev, price_monthly: parseFloat(e.target.value) || 0 }))}
                    data-testid="price-monthly-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price_yearly">Yearly Price ($)</Label>
                  <Input
                    id="price_yearly"
                    type="number"
                    min="0"
                    value={formData.price_yearly}
                    onChange={(e) => setFormData(prev => ({ ...prev, price_yearly: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
              </div>
            </div>

            {/* Limits — Seat Limit and Storage are the only two numbers an
                admin needs 95% of the time. Maximum Users / Maximum Storage
                (GB) are derived from these automatically (see
                buildSavePayload) unless explicitly overridden below. */}
            <div>
              <h4 className="text-sm font-medium mb-3">Limits</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="seat_limit">Seat Limit</Label>
                  <Input
                    id="seat_limit"
                    type="number"
                    min="1"
                    value={formData.seat_limit}
                    onChange={(e) => setFormData(prev => ({ ...prev, seat_limit: parseInt(e.target.value) || 1 }))}
                    data-testid="seat-limit-input"
                  />
                  <div className="flex items-center justify-between pt-1">
                    <button
                      type="button"
                      className="text-xs text-indigo-600 hover:underline"
                      onClick={() => setOverrideMaxUsers(v => !v)}
                      data-testid="toggle-override-max-users"
                    >
                      {overrideMaxUsers ? 'Remove override' : 'Advanced override'}
                    </button>
                  </div>
                  {overrideMaxUsers && (
                    <div className="space-y-1 pt-1">
                      <Label htmlFor="max_users_override" className="text-xs font-normal text-slate-600">
                        Maximum Users (independent of Seat Limit)
                      </Label>
                      <Input
                        id="max_users_override"
                        type="number"
                        min="1"
                        placeholder={`Default: ${formData.seat_limit}`}
                        value={formData.default_limits.MAX_USERS ?? ''}
                        onChange={(e) => setLimitDefault('MAX_USERS', e.target.value)}
                        data-testid="max-users-override-input"
                      />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="storage_limit_gb">Storage (GB)</Label>
                  <Input
                    id="storage_limit_gb"
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={Math.round((formData.storage_limit_mb / 1024) * 100) / 100}
                    onChange={(e) => {
                      const gb = parseFloat(e.target.value);
                      setFormData(prev => ({ ...prev, storage_limit_mb: Math.round((Number.isNaN(gb) ? 0.1 : gb) * 1024) }));
                    }}
                    data-testid="storage-limit-gb-input"
                  />
                  <div className="flex items-center justify-between pt-1">
                    <button
                      type="button"
                      className="text-xs text-indigo-600 hover:underline"
                      onClick={() => setOverrideMaxStorage(v => !v)}
                      data-testid="toggle-override-max-storage"
                    >
                      {overrideMaxStorage ? 'Remove override' : 'Advanced override'}
                    </button>
                  </div>
                  {overrideMaxStorage && (
                    <div className="space-y-1 pt-1">
                      <Label htmlFor="max_storage_override" className="text-xs font-normal text-slate-600">
                        Maximum Storage GB (independent of Storage)
                      </Label>
                      <Input
                        id="max_storage_override"
                        type="number"
                        min="0.1"
                        step="0.1"
                        placeholder={`Default: ${Math.round((formData.storage_limit_mb / 1024) * 100) / 100}`}
                        value={formData.default_limits.MAX_STORAGE_GB ?? ''}
                        onChange={(e) => setLimitDefault('MAX_STORAGE_GB', e.target.value)}
                        data-testid="max-storage-override-input"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Advanced Limits — the remaining quota ceilings applied to
                every tenant on this plan (tenant_limits, via the Limits &
                Quotas engine). Driven entirely by the standard-limits
                catalog, so a new quota key added there appears here
                automatically. Leave a field blank to fall back to that
                key's platform default. MAX_USERS/MAX_STORAGE_GB are
                excluded here -- they're handled by the overrides above. */}
            <Collapsible open={showAdvancedLimits} onOpenChange={setShowAdvancedLimits}>
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start gap-2 px-0 text-slate-600 hover:bg-transparent hover:text-slate-900"
                  data-testid="plan-advanced-limits-toggle"
                >
                  {showAdvancedLimits ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  Advanced Limits
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <p className="text-xs text-slate-500 mb-2">Applied to every tenant on this plan. Sensible defaults are pre-filled; override only if this plan needs a different ceiling.</p>
                {standardLimits.length === 0 ? (
                  <p className="text-xs text-slate-400">Loading quota catalog...</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                    {standardLimits.filter((def) => def.limit_key !== 'MAX_USERS' && def.limit_key !== 'MAX_STORAGE_GB').map((def) => (
                      <div key={def.limit_key} className="space-y-1">
                        <Label htmlFor={`limit-${def.limit_key}`} className="text-xs font-normal text-slate-600" title={def.description}>
                          {def.name}
                        </Label>
                        <Input
                          id={`limit-${def.limit_key}`}
                          type="number"
                          min="-1"
                          placeholder={`Default: ${def.default_value}`}
                          value={formData.default_limits[def.limit_key] ?? ''}
                          onChange={(e) => setLimitDefault(def.limit_key, e.target.value)}
                          data-testid={`plan-limit-${def.limit_key}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* Products */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium">
                  Products ({getSellableProducts(availableModules).filter(m => formData.enabled_modules.includes(m.api_name)).length} selected)
                </h4>
                <p className="text-xs text-slate-500">
                  Bundled capabilities are included automatically
                </p>
              </div>
              {renderModuleSelector()}
            </div>

            {/* Status */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Active</Label>
                <p className="text-xs text-slate-500">Make this plan available for tenants</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !formData.name || !formData.api_name}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-plan-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {dialogMode === 'create' ? 'Create Plan' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Plan confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This plan will no longer be offered to new tenants. Tenants already on this plan are not affected —
              the plan is deactivated, not deleted, so their assignment keeps working.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600" disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SubscriptionsPage;
